import { useState, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "../lib/api";

const VERB = {
  ACKNOWLEDGED: "acknowledged",
  DISMISSED: "dismissed",
  EXPLAINED: "explained",
  CORRECTED: "corrected",
};

// Shared multi-select workflow-review behaviour (select findings, add a shared
// note, apply a review status in bulk, with an Undo toast). Used anywhere the
// app exposes a bulk workflow comment (Worklist, Error Detail, ...).
export function useBulkReview({ onRefresh } = {}) {
  const [selected, setSelected] = useState(() => new Set());
  const [note, setNote] = useState("");
  const restoringRef = useRef(false);

  const toggle = (guid) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(guid) ? next.delete(guid) : next.add(guid);
      return next;
    });
  const clear = () => setSelected(new Set());
  const selectAll = (guids) => setSelected(new Set(guids));

  const restore = async (previous) => {
    if (restoringRef.current) return;
    restoringRef.current = true;
    try {
      await api.post(`/findings/bulk-restore`, { items: previous });
      toast.success(`${previous.length} finding${previous.length === 1 ? "" : "s"} restored`);
      onRefresh?.();
    } catch {
      toast.error("Undo failed");
    } finally {
      restoringRef.current = false;
    }
  };

  const bulk = useMutation({
    mutationFn: async (status) =>
      (await api.post(`/findings/bulk-review`, {
        review_status: status,
        reviewer_note: note.trim(),
        check_result_GUIDs: [...selected],
      })).data,
    onSuccess: (res, status) => {
      toast.success(`${res.updated} finding${res.updated === 1 ? "" : "s"} ${VERB[status] || "updated"}`, {
        action: res.previous?.length ? { label: "Undo", onClick: () => restore(res.previous) } : undefined,
      });
      clear();
      setNote("");
      onRefresh?.();
    },
    onError: () => toast.error("Bulk update failed"),
  });

  return { selected, setSelected, toggle, clear, selectAll, note, setNote, bulk };
}
